// Per-module result-card renderers used by ResultsPageLayout.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Plane, Clock, MapPin, Star, Users, Train, Bus, Home, Package, FileText, Ship, Banknote, ShieldCheck, Check, Heart, Coffee, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';
import { useWishlist } from '../../lib/wishlist';
import { recordFlightView } from '../../lib/recent';
import { saveHotelPreview } from '../../lib/hotelCache';
import NoImagePlaceholder from '../hotels/NoImagePlaceholder';

const BookBtn = ({ onClick, testId, label = 'Book Now', className = '' }) => (
  <Button data-testid={testId} onClick={onClick} className={`bg-red-600 hover:bg-red-700 text-white ${className}`}>
    {label}
  </Button>
);

const WishlistButton = ({ id, testId }) => {
  const { has, toggle } = useWishlist();
  const active = has(id);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggle(id); }}
      data-testid={testId}
      aria-label={active ? 'Remove from wishlist' : 'Add to wishlist'}
      className={`absolute top-2 right-2 z-10 grid place-items-center h-8 w-8 rounded-full backdrop-blur-md shadow-md transition-all
        ${active ? 'bg-red-600 text-white' : 'bg-white/85 text-gray-700 hover:bg-white'}`}
    >
      <Heart className={`h-4 w-4 ${active ? 'fill-current' : ''}`} />
    </button>
  );
};

export const FlightCard = ({ flight }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  return (
    <Card data-testid={`flight-card-${flight.fare_id}`} className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-1.5 rounded-lg w-12 h-12 flex items-center justify-center shadow-sm flex-shrink-0">
                <img
                  src={`https://images.kiwi.com/airlines/64/${flight.airline_code}.png`}
                  alt={flight.airline}
                  className="max-h-9 max-w-9 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                />
                <Plane className="h-5 w-5 text-red-600 dark:text-red-400 hidden" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-base text-gray-900 dark:text-white truncate">{flight.airline}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{flight.flight_number}</p>
              </div>
              <Badge variant="secondary" className="dark:bg-gray-800 dark:text-gray-200">{flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop`}</Badge>
              {flight.is_refundable && <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 hover:bg-green-100">Refundable</Badge>}
            </div>
            <div className="grid grid-cols-3 gap-3 items-center">
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{flight.departure}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">{flight.from_code}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{flight.duration}</p>
                <div className="border-t-2 border-dashed border-gray-300 dark:border-gray-700 relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-white dark:bg-gray-900 px-1.5">
                    <Clock className="h-3.5 w-3.5 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{flight.arrival}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">{flight.to_code}</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
              {flight.cabin_class} · {flight.seats_available} seats left · Cabin {flight.baggage?.cabin} · Check-in {flight.baggage?.checkin}
            </p>
          </div>
          <div className="text-right md:min-w-[160px]">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatPrice(flight.display_price)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">incl. taxes & fees</p>
            <BookBtn
              onClick={() => {
                recordFlightView({
                  id: flight.fare_id,
                  origin: flight.from_code,
                  destination: flight.to_code,
                  departure_date: flight.departure_date || flight.travel_date,
                  airline: flight.airline,
                  price: flight.display_price,
                  currency: flight.currency || 'INR',
                });
                navigate(`/booking/flights/${flight.fare_id}`, { state: { item: flight } });
              }}
              testId={`book-flight-${flight.fare_id}`}
              className="w-full"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Meal-plan label — derived ONLY from ETG's raw `meal` code.
// The mapping is a code→English label translation, not fabricated data.
const MEAL_LABELS = {
  breakfast: 'Breakfast included',
  'breakfast-buffet': 'Breakfast (buffet)',
  'half-board': 'Half board',
  'full-board': 'Full board',
  'all-inclusive': 'All inclusive',
  'ultra-all-inclusive': 'Ultra all-inclusive',
  'continental-breakfast': 'Continental breakfast',
  'american-breakfast': 'American breakfast',
  dinner: 'Dinner included',
};
export const mealLabel = (raw) => {
  if (!raw) return '';
  const key = String(raw).toLowerCase();
  if (key === 'nomeal' || key === 'no-meal' || key === 'ro') return '';
  return MEAL_LABELS[key] || '';
};
const includesBreakfast = (raw) => /^(breakfast|breakfast-buffet|continental-breakfast|american-breakfast|half-board|full-board|all-inclusive|ultra-all-inclusive)/i.test(String(raw || ''));

// Cancellation deadline formatter — takes ETG's ISO timestamp verbatim.
export const fmtCancelDeadline = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

// ─────────────────────────────────────────────────────────────────
// MMT-inspired HotelCard
// LEFT: image carousel (5 photos, prev/next arrows, hide at ends) + wishlist heart
// CENTER: name, location, rating chip, amenities, cancellation/breakfast tags
// RIGHT: original price (struck), discount %, final price, taxes, CTA
// ─────────────────────────────────────────────────────────────────

// Order supplier-provided gallery images. If a `fallback` single-image
// field is present but not already in the gallery, prepend it. NO stock
// or placeholder images are used — empty gallery → carousel renders the
// NoImagePlaceholder state instead. Required for honest supplier data.
const orderGallery = (gallery, fallback) => {
  const arr = Array.isArray(gallery) && gallery.length > 0 ? [...gallery] : [];
  if (arr.length === 0 && fallback) arr.push(fallback);
  // Prefer an exterior-looking URL first when hints exist in the URL.
  const idx = arr.findIndex((u) => /exterior|facade|outside|building|outdoor|dusk/i.test(String(u || '')));
  if (idx > 0) {
    const [pick] = arr.splice(idx, 1);
    arr.unshift(pick);
  }
  return arr.slice(0, 8);
};

const HotelImageCarousel = ({ images, alt, hotelId, badge }) => {
  const [idx, setIdx] = React.useState(0);
  const total = images.length;

  if (total === 0) {
    // No supplier images → honest empty state (never a stock image).
    return (
      <div className="relative w-full h-full">
        <NoImagePlaceholder />
        {badge}
      </div>
    );
  }

  const safeIdx = Math.min(idx, total - 1);
  const prev = (e) => { e.stopPropagation(); setIdx((i) => Math.max(0, i - 1)); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => Math.min(total - 1, i + 1)); };
  return (
    <div className="relative w-full h-full bg-gray-100 dark:bg-gray-800 group">
      <img
        src={images[safeIdx]}
        alt={alt}
        loading="lazy"
        data-testid={`hotel-image-${hotelId}-${safeIdx}`}
        className="w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
      />
      {/* Left arrow — hidden on first image */}
      {safeIdx > 0 && (
        <button
          type="button"
          onClick={prev}
          data-testid={`hotel-image-prev-${hotelId}`}
          aria-label="Previous photo"
          className="absolute left-2 top-1/2 -translate-y-1/2 grid place-items-center h-8 w-8 rounded-full bg-white/90 hover:bg-white text-gray-800 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {/* Right arrow — hidden on last image */}
      {safeIdx < total - 1 && (
        <button
          type="button"
          onClick={next}
          data-testid={`hotel-image-next-${hotelId}`}
          aria-label="Next photo"
          className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center h-8 w-8 rounded-full bg-white/90 hover:bg-white text-gray-800 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
      {/* Dot indicator */}
      {total > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
          {images.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === safeIdx ? 'w-3 bg-white' : 'w-1.5 bg-white/60'}`} />
          ))}
        </div>
      )}
      {badge}
    </div>
  );
};

export const HotelCard = ({ hotel }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const price = Number(hotel.display_price ?? hotel.price ?? 0);
  const original = Number(hotel.original_price ?? hotel.list_price ?? 0);
  const hasDiscount = original > price && price > 0;
  const discountPct = hasDiscount ? Math.round(((original - price) / original) * 100) : 0;
  const rating = Number(hotel.rating ?? 0);
  const reviews = Number(hotel.reviews ?? 0);
  const amenities = hotel.amenities || [];
  // Data-integrity rules:
  //   • Free cancellation is shown ONLY when ETG explicitly returns
  //     has_free_cancellation === true (backed by free_cancellation_before).
  //   • Breakfast is shown ONLY when the rate's `meal` code is a breakfast
  //     or higher meal plan — not from generic amenity strings.
  const cancelDeadline = hotel.free_cancellation_before || null;
  const showFreeCancel = hotel.has_free_cancellation === true && !!cancelDeadline;
  const showBreakfast = includesBreakfast(hotel.meal || hotel.board_name || '');
  const mealText = mealLabel(hotel.meal || hotel.board_name || '');
  // Total price / per-night toggle (injected by ResultsPageLayout)
  const preference = hotel.__preference || 'per_night';
  const nights = Math.max(1, Number(hotel.__nights || 1));
  const showTotal = preference === 'total';
  const displayPrice = showTotal ? price * nights : price;
  const displayOriginal = showTotal ? original * nights : original;
  const images = orderGallery(hotel.gallery || hotel.images, hotel.image);
  // MMT-style: round star count (1-5) for the row of stars next to the title.
  const starCount = Math.max(0, Math.min(5, Math.round(rating)));
  // Verbal rating tier for the top-right chip (MMT: Very Good, Excellent, Good).
  const ratingLabel = rating >= 4.5 ? 'Excellent' : rating >= 4.0 ? 'Very Good' : rating >= 3.5 ? 'Good' : rating >= 3.0 ? 'Pleasant' : 'Rated';

  return (
    <Card
      data-testid={`hotel-card-${hotel.id}`}
      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-xl hover:border-red-200 dark:hover:border-red-800 transition-all duration-300 overflow-hidden rounded-2xl"
    >
      <div className="flex flex-col md:flex-row h-full md:h-56">
        {/* LEFT — image carousel with fixed aspect — keeps every card at the same height */}
        <div className="relative md:w-[280px] md:flex-shrink-0 h-48 md:h-full">
          <HotelImageCarousel
            images={images}
            alt={hotel.name}
            hotelId={hotel.id}
            badge={
              <>
                <WishlistButton id={hotel.id} testId={`wishlist-hotel-${hotel.id}`} />
                {hasDiscount && (
                  <div className="absolute top-2 left-2 bg-emerald-600 text-white text-[11px] font-bold px-2 py-0.5 rounded shadow-sm">
                    {discountPct}% OFF
                  </div>
                )}
              </>
            }
          />
        </div>

        {/* CENTER — details */}
        <CardContent className="flex-1 p-4 md:p-5 min-w-0 overflow-hidden">
          <div className="flex items-start gap-2 flex-wrap">
            <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white truncate" data-testid={`hotel-name-${hotel.id}`}>
              {hotel.name}
            </h3>
            {starCount > 0 && (
              <div className="flex items-center gap-0.5 mt-0.5 shrink-0" data-testid={`hotel-stars-${hotel.id}`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-3.5 w-3.5 ${i < starCount ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} />
                ))}
              </div>
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1 mt-1">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{[hotel.area, hotel.city || hotel.location].filter(Boolean).join(', ')}</span>
          </p>

          {hotel.room_type && (
            <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 font-medium mt-2 truncate border-l-2 border-blue-500 pl-2">
              1 x ({hotel.room_type})
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px] sm:text-xs">
            {showFreeCancel && (
              <span
                className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-medium"
                data-testid={`hotel-free-cancel-${hotel.id}`}
                title={`Free cancellation until ${fmtCancelDeadline(cancelDeadline)}`}
              >
                <Check className="h-3 w-3" /> Free cancellation until {fmtCancelDeadline(cancelDeadline)}
              </span>
            )}
            {showBreakfast && (
              <span
                className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-medium"
                data-testid={`hotel-breakfast-${hotel.id}`}
              >
                <Coffee className="h-3 w-3" /> {mealText}
              </span>
            )}
            {amenities.slice(0, 2).map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                <Check className="h-3 w-3 text-gray-400" /> {a}
              </span>
            ))}
          </div>
        </CardContent>

        {/* RIGHT — rating (top) + price + CTA (bottom) — MMT style */}
        <div className="border-t md:border-t-0 md:border-l border-dashed border-gray-200 dark:border-gray-700 p-4 md:p-5 md:w-[200px] flex flex-row md:flex-col items-end md:items-stretch justify-between md:justify-between gap-3">
          {/* Rating chip — top-right */}
          {rating > 0 ? (
            <div className="text-right" data-testid={`hotel-rating-${hotel.id}`}>
              <div className="inline-flex items-center gap-1.5">
                <span className="text-xs sm:text-sm font-semibold text-blue-700 dark:text-blue-300">{ratingLabel}</span>
                <span className="bg-blue-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">{rating.toFixed(1)}</span>
              </div>
              {reviews > 0 && (
                <p className="text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">({reviews.toLocaleString('en-IN')} Ratings)</p>
              )}
            </div>
          ) : <span />}

          {/* Price block — bottom-right */}
          <div className="text-right flex flex-col items-end">
            {hasDiscount && (
              <p className="text-xs text-gray-400 line-through" data-testid={`hotel-mrp-${hotel.id}`}>
                {formatPrice(displayOriginal)}
              </p>
            )}
            <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight" data-testid={`hotel-price-${hotel.id}`}>
              {formatPrice(displayPrice)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">incl. taxes &amp; fees</p>
            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
              {showTotal ? `total · ${nights} night${nights > 1 ? 's' : ''}` : 'Per Night'}
            </p>
            <BookBtn
              onClick={() => {
                // Open Hotel Details in a NEW tab so the search results tab
                // remains intact (filters, sort, scroll, applied criteria).
                // We forward the current search URL (check-in/out, rooms,
                // guests, city, lat/lng) so the details page can re-hydrate
                // rates via the backend if localStorage isn't available.
                // rel="noopener,noreferrer" for tab-isolation + phishing safety.
                saveHotelPreview(hotel.id, hotel);
                const qs = window.location.search || '';
                const url = `/hotels/${hotel.id}${qs}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              testId={`book-hotel-${hotel.id}`}
              label="View Rooms"
              className="md:w-full mt-2"
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export const BusCard = ({ bus }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  return (
    <Card data-testid={`bus-card-${bus.id}`} className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-red-100 dark:bg-red-900/40 p-2 rounded-lg"><Bus className="h-5 w-5 text-red-600 dark:text-red-400" /></div>
              <div>
                <h3 className="font-bold text-base text-gray-900 dark:text-white">{bus.operator}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{bus.bus_type}</p>
              </div>
              <div className="flex items-center gap-1 ml-auto"><Star className="h-4 w-4 text-yellow-500 fill-current" /><span className="text-sm font-semibold text-gray-900 dark:text-white">{bus.rating}</span></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><p className="text-xl font-bold text-gray-900 dark:text-white">{bus.departure}</p><p className="text-xs text-gray-600 dark:text-gray-300">{bus.from}</p></div>
              <div className="text-center"><p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{bus.duration}</p><div className="border-t-2 border-dashed border-gray-300 dark:border-gray-700" /></div>
              <div className="text-right"><p className="text-xl font-bold text-gray-900 dark:text-white">{bus.arrival}</p><p className="text-xs text-gray-600 dark:text-gray-300">{bus.to}</p></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(bus.amenities || []).slice(0, 5).map((a, i) => <Badge key={i} variant="outline" className="text-[10px] dark:border-gray-700 dark:text-gray-300">{a}</Badge>)}
            </div>
          </div>
          <div className="text-right md:min-w-[140px]">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatPrice(bus.display_price ?? bus.net_fare_per_seat ?? bus.price)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{bus.seats_available} seats left</p>
            <BookBtn onClick={() => navigate(`/booking/buses/${bus.id}`)} testId={`book-bus-${bus.id}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const TrainCard = ({ train }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  return (
    <Card data-testid={`train-card-${train.id}`} className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-red-100 dark:bg-red-900/40 p-2 rounded-lg"><Train className="h-5 w-5 text-red-600 dark:text-red-400" /></div>
              <div><h3 className="font-bold text-base text-gray-900 dark:text-white">{train.trainName}</h3><p className="text-xs text-gray-600 dark:text-gray-400">{train.trainNumber}</p></div>
              <Badge variant="secondary" className="ml-auto dark:bg-gray-800">{train.type}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><p className="text-xl font-bold text-gray-900 dark:text-white">{train.departure}</p><p className="text-xs text-gray-600 dark:text-gray-300">{train.from}</p></div>
              <div className="text-center"><p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{train.duration}</p><div className="border-t-2 border-dashed border-gray-300 dark:border-gray-700" /></div>
              <div className="text-right"><p className="text-xl font-bold text-gray-900 dark:text-white">{train.arrival}</p><p className="text-xs text-gray-600 dark:text-gray-300">{train.to}</p></div>
            </div>
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">Class: {train.class} · {train.seats} seats available</p>
          </div>
          <div className="text-right md:min-w-[140px]">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatPrice(train.price)}</p>
            <p className="text-xs text-gray-500 mb-2">per passenger</p>
            <BookBtn onClick={() => navigate(`/booking/trains/${train.id}`)} testId={`book-train-${train.id}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const CabCard = ({ cab }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  return (
    <Card data-testid={`cab-card-${cab.id}`} className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300">
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <img src={cab.image} alt={cab.model} className="w-28 h-20 object-cover rounded-lg" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1"><h3 className="font-bold text-base text-gray-900 dark:text-white">{cab.model}</h3><Badge className="bg-red-600 text-white">{cab.type}</Badge></div>
              <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-yellow-500 fill-current" />{cab.rating}</span>
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{cab.capacity} Seater</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(cab.features || []).slice(0, 4).map((f, i) => <Badge key={i} variant="outline" className="text-[10px] dark:border-gray-700 dark:text-gray-300">{f}</Badge>)}
              </div>
            </div>
          </div>
          <div className="text-right md:min-w-[140px]">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatPrice(cab.price)}</p>
            <p className="text-xs text-gray-500 mb-2">{cab.priceUnit}</p>
            <BookBtn onClick={() => navigate(`/booking/cabs/${cab.id}`)} testId={`book-cab-${cab.id}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Compact card for the longer-form services (villas, packages, tours, cruise, visa, forex, insurance)
const InfoCard = ({ item, bookHref, testIdPrefix, icon: Icon, title, subtitle, badges = [], features = [], priceLabel = 'starting from', priceUnit }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  return (
    <Card data-testid={`${testIdPrefix}-card-${item.id}`} className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300 overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {item.image && (
          <div className="relative md:w-1/3 h-48 md:h-auto">
            <img src={item.image} alt={title} className="w-full h-full object-cover" />
            <WishlistButton id={`${testIdPrefix}-${item.id}`} testId={`wishlist-${testIdPrefix}-${item.id}`} />
          </div>
        )}
        <CardContent className="flex-1 p-5">
          <div className="flex justify-between items-start gap-3 mb-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon className="h-4 w-4 text-red-600 dark:text-red-400" />}
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">{title}</h3>
              </div>
              {subtitle && <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
            </div>
            {item.rating != null && (
              <div className="text-right flex-shrink-0">
                <div className="flex items-center gap-1"><Star className="h-4 w-4 text-yellow-500 fill-current" /><span className="font-bold text-gray-900 dark:text-white">{item.rating}</span></div>
                {item.reviews && <p className="text-[10px] text-gray-500">{item.reviews} reviews</p>}
              </div>
            )}
          </div>
          {badges.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">{badges.map((b, i) => <Badge key={i} className="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-50">{b}</Badge>)}</div>}
          {features.length > 0 && (
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-400 mb-3">
              {features.slice(0, 6).map((f, i) => <li key={i} className="flex items-center gap-1"><Check className="h-3 w-3 text-green-600" /> {f}</li>)}
            </ul>
          )}
          <div className="flex items-end justify-between border-t border-gray-100 dark:border-gray-800 pt-3 mt-3">
            <p className="text-xs text-gray-500">{priceLabel}</p>
            <div className="text-right">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatPrice(item.price || 0)}</p>
              {priceUnit && <p className="text-xs text-gray-500 mb-2">{priceUnit}</p>}
              <BookBtn onClick={() => navigate(bookHref)} testId={`book-${testIdPrefix}-${item.id}`} className="ml-auto" />
            </div>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

export const VillaCard = ({ item }) => (
  <InfoCard item={item} bookHref={`/booking/villas/${item.id}`} testIdPrefix="villa" icon={Home}
    title={item.name} subtitle={`${item.area || ''}${item.area ? ', ' : ''}${item.location || ''}`}
    badges={[item.type].filter(Boolean)} features={item.amenities} priceLabel="per night" />
);

export const PackageCard = ({ item }) => (
  <InfoCard item={item} bookHref={`/booking/packages/${item.id}`} testIdPrefix="package" icon={Package}
    title={item.name} subtitle={`${item.destination} · ${item.duration}`}
    badges={[item.duration].filter(Boolean)} features={item.highlights} priceLabel="per person" />
);

export const TourCard = ({ item }) => (
  <InfoCard item={item} bookHref={`/booking/tours/${item.id}`} testIdPrefix="tour" icon={MapPin}
    title={item.name} subtitle={`${item.location} · ${item.duration}`}
    features={item.inclusions} priceLabel="per person" />
);

export const VisaCard = ({ item }) => (
  <InfoCard item={item} bookHref={`/booking/visa/${item.id}`} testIdPrefix="visa" icon={FileText}
    title={item.country} subtitle={item.type}
    badges={[item.processingTime].filter(Boolean)} features={item.documents} priceLabel="per applicant" />
);

export const CruiseCard = ({ item }) => (
  <InfoCard item={item} bookHref={`/booking/cruise/${item.id}`} testIdPrefix="cruise" icon={Ship}
    title={item.name} subtitle={`${item.route} · ${item.duration}`}
    badges={[item.cabinType].filter(Boolean)} features={item.amenities} priceLabel="per person" />
);

export const ForexCard = ({ item }) => (
  <InfoCard item={item} bookHref={`/booking/forex/${item.id}`} testIdPrefix="forex" icon={Banknote}
    title={`${item.currency} · ${item.type}`} subtitle={item.provider}
    badges={item.rate ? [`@ ₹${item.rate}`] : []} features={item.features}
    priceLabel={item.service || ''} />
);

export const InsuranceCard = ({ item }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  return (
    <Card data-testid={`insurance-card-${item.id}`} className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300">
      <CardContent className="p-5">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-5 w-5 text-red-600 dark:text-red-400" />
              <h3 className="font-bold text-lg text-gray-900 dark:text-white">{item.name}</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{item.provider} · {item.duration}</p>
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-100">Coverage: {item.coverage}</Badge>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
              {(item.features || []).map((f, i) => <li key={i} className="flex items-center gap-1"><Check className="h-3 w-3 text-green-600" /> {f}</li>)}
            </ul>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatPrice(item.price)}</p>
            <p className="text-xs text-gray-500 mb-2">premium</p>
            <BookBtn onClick={() => navigate(`/booking/insurance/${item.id}`)} testId={`book-insurance-${item.id}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
